import React from "react";
import QRCode from "react-qr-code";
import { Download } from "lucide-react";
import { TIER_META } from "@/lib/format";
import { HscLogo } from "@/components/HscLogo";
import { Button } from "@/components/kit";
import { memberQrValue, membershipSummary, typeLabel, fmtDateID, downloadMemberCardPng } from "@/lib/memberCard";

// Premium digital member card. QR encodes the SAME identity as mobile: `HSC-<member.id>-<tier>`.
export default function MemberCard({ member, memberships = [], showDownload = true, onDownload }) {
  const meta = TIER_META[member.tier] || TIER_META.Bronze;
  const summary = membershipSummary(memberships);
  const qrValue = memberQrValue(member);

  const handleDownload = async () => {
    try { await downloadMemberCardPng(member, memberships); onDownload && onDownload(true); }
    catch { onDownload && onDownload(false); }
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid="member-card">
      <div className="mx-auto w-full max-w-[360px] overflow-hidden rounded-3xl border p-6"
        style={{ background: meta.bg, borderColor: meta.border }}>
        <div className="flex items-center justify-between">
          <HscLogo size="text-lg" />
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#6E6E73]">Tier</div>
            <div className="text-sm font-bold uppercase tracking-widest" style={{ color: meta.accent }}>{member.tier}</div>
          </div>
        </div>

        <div className="mt-5 text-center">
          <div className="font-display text-2xl text-white" data-testid="member-card-name">{member.name}</div>
          <div className="mt-1 text-xs text-[#A1A1A6]">{typeLabel(summary)}</div>
          <span className="mt-2 inline-block rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider"
            style={{ color: summary.color, background: summary.color + "22" }} data-testid="member-card-status">
            {summary.status}
          </span>
        </div>

        <div className="mt-5 flex justify-center">
          <div className="rounded-2xl bg-white p-4">
            <QRCode value={qrValue} size={190} bgColor="#ffffff" fgColor="#000000" data-testid="member-card-qr" />
          </div>
        </div>

        <div className="mt-5 space-y-3 border-t border-white/10 pt-4 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6E6E73]">Member ID</div>
            <div className="mt-0.5 break-all font-mono text-[12px] text-white">{String(member.id).toUpperCase()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6E6E73]">
              {summary.status === "EXPIRED" ? "Expired" : "Valid Until"}
            </div>
            <div className="mt-0.5 text-sm font-semibold" style={{ color: summary.status === "INACTIVE" ? "#8E8E93" : summary.color }}>
              {summary.validUntil ? fmtDateID(summary.validUntil) : "—"}
            </div>
          </div>
        </div>
      </div>

      {showDownload && (
        <Button variant="outline" onClick={handleDownload} data-testid="download-member-card">
          <Download className="h-4 w-4" /> Download Member Card (PNG)
        </Button>
      )}
    </div>
  );
}
